import React from "react";
import BaseModal from "../../../../components/ui/BaseModal";
import BaseDragDrop from "../../../../components/ui/BaseDragDrop";
import BaseButton from "../../../../components/ui/BaseButton";
import * as yup from "yup";
import { useFormik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { deleteUserResume, uploadUserResume } from "../../../../services/api/digitalAsset";
import { useDispatch } from "react-redux";
import { showToast } from "../../../../store/toast/toastSlice";
import { FILE_MAX_UPLOAD_SIZE } from "../../../../utils/helpers";

const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const validationSchema = yup.object({
  file: yup
    .mixed()
    .required("File is required")
    .test(
      "fileSize",
      "File must be 2MB or smaller",
      (value) => value && value.size <= FILE_MAX_UPLOAD_SIZE,
    )
    .test(
      "fileType",
      "Unsupported format. Use PDF, DOC, or DOCX",
      (value) => value && ALLOWED_TYPES.includes(value.type),
    ),
});

const AddEditResumeModal = ({ open, close, existingResume, refetchMe, targetUserId }) => {
  const dispatch = useDispatch();

  const { mutateAsync: deleteResumeMutation } = useMutation({
    mutationFn: () => deleteUserResume(targetUserId),
    enabled: false,
  });

  const { isPending: isUploading, mutateAsync: uploadResumeMutation } = useMutation({
    mutationFn: (file) => uploadUserResume(file, targetUserId),
    enabled: false,
  });

  const formik = useFormik({
    initialValues: { file: null },
    validationSchema,
    onSubmit: handleUpload,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  async function handleUpload() {
    if (!formik.values.file) return;
    try {
      if (existingResume) {
        await deleteResumeMutation();
      }
      await uploadResumeMutation(formik.values.file);
      dispatch(showToast({ message: "Resume uploaded successfully", type: "success" }));
      close(false);
      refetchMe();
    } catch (error) {
      dispatch(showToast({ message: "Failed to upload resume", type: "error" }));
    }
  }

  const handleFileChange = (file) => {
    formik.setFieldValue("file", file[0]);
  };

  return (
    <form>
      <BaseModal
        className="-top-[200px] overflow-auto"
        open={open}
        close={close}
        style={{ width: "1000px" }}
      >
        <div className="py-7 px-12">
          <h6 className="text-dark font-normal border-b pb-1">Upload Resume</h6>

          <BaseDragDrop className="bg-white mt-8" onDrop={handleFileChange}>
            <div className="flex flex-col items-center py-5">
              <i className="fa-regular fa-file-lines text-primary text-5xl mb-1"></i>
              <p className="text-dark font-normal mb-2 text-base">
                Upload your resume here (PDF, DOC, DOCX)
              </p>
              <div>
                <BaseButton color="primary" textSize="text-base" className="py-[6px]">
                  Choose File
                </BaseButton>
              </div>
            </div>
          </BaseDragDrop>

          {formik.values.file && (
            <div className="flex items-center bg-light rounded-md py-2 px-3 mt-3">
              <i className="fa-regular fa-file-pdf text-xl text-red-500"></i>
              <p className="ml-3">{formik.values.file.name}</p>
            </div>
          )}
          {formik.errors.file && <p className="text-red-500 mt-2">{formik.errors.file}</p>}
        </div>

        <div
          className="d-flex content-center bg-white py-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            disabled={!!formik.errors.file || !formik.values.file}
            loaderSize="22px"
            isLoading={isUploading}
            color="primary"
            className="py-[5px] px-[20px] min-w-32"
            type="submit"
            onClick={formik.handleSubmit}
          >
            Save Changes
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
};

export default AddEditResumeModal;
